<script setup lang="ts">
import { iconColors } from 'assets/js/utils';
import { BriefcaseBusiness, HandCoins, LogIn, LogOut, TextSearch, X } from 'lucide-vue-next';
import HeaderAvatar from '../common/HeaderAvatar.vue';
import { vOnClickOutside } from '@vueuse/components';

interface DrawerItem {
  title: string;
  icon: typeof BriefcaseBusiness;
  route?: string;
  onclick: () => void;
}

const route = useRoute();
const userStore = useUserStore();
const authStore = useAuthStore();

const drawerToggleRef = ref();
const isOpen = ref(false);

const onClose = () => (isOpen.value = false);

const items: Array<DrawerItem> = [
  { title: 'Мои заказы', route: '/profile#projects', icon: BriefcaseBusiness, onclick: onClose },
  { title: 'Темы проектов', route: '/topics', icon: TextSearch, onclick: onClose },
  { title: 'Программа лояльности', route: '/loyalty/levels', icon: HandCoins, onclick: onClose }
]

const handleOnClickOutside = [() => (isOpen.value = false), { ignore: [drawerToggleRef] }];

const onLogout = () => {
  authStore.logout();

  setTimeout(() => {
    if (route.name === 'profile') {
      navigateTo('/');
    } else {
      reloadNuxtApp();
    }
  }, 0);

  userStore.user = undefined;

  onClose();
};
</script>

<template>
  <v-button
    v-if="Boolean(!userStore.user)"
    schema="transparent"
    class="header-login-button"
    @click="authStore.handleIsOpened('login')"
  >
    <LogIn
      :size="24"
      :color="iconColors.primary"
    />
  </v-button>

  <div
    v-else
    ref="drawerToggleRef"
    class="header-drawer-toggle"
    @click="isOpen = !isOpen"
  >
    <transition name="fade">
      <HeaderAvatar v-if="!isOpen" />
      <X
        v-else
        :size="24"
        :color="iconColors.primary"
      />
    </transition>
  </div>

  <ul
    v-if="Boolean(userStore.user)"
    v-on-click-outside="handleOnClickOutside"
    class="header-drawer"
    :class="{ open: isOpen }"
  >
    <li
      v-for="item in items"
      :key="item.title"
    >
      <nuxt-link
        :to="item.route"
        @click="item.onclick"
      >
        <component
          :is="item.icon"
          :color="iconColors.primary"
          :size="20"
        />

        {{ item.title }}
      </nuxt-link>
    </li>
    <li>
      <nuxt-link @click.prevent="onLogout">
        <LogOut
          :color="iconColors.primary"
          :size="20"
        />

        Выйти
      </nuxt-link>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
.header-login-button {
  padding: 0;
  width: 40px;
  height: 40px;
}

.header-drawer-toggle {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    position: absolute;
  }
}

.header-drawer {
  position: absolute;
  top: -300px;
  width: 100%;
  left: 0px;
  background: white;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;
  padding: 56px 26px 6px 26px;
  z-index: -1;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.09);
  transition: all 0.3s ease-in-out;

  &.open {
    top: 0px;
  }

  li {
    line-height: 24px;
    padding: 12px 0;

    a {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 16px;
      font-weight: 400;
      color: black;
    }
  }
}
</style>
